import {
  CreateTransactionsObject,
  GetCashflowSeriesProps,
  GetTransactionsProps,
  GetGroupedTransactionsProps,
  GetTransactionsTotalsProps,
} from "../../pages/Transactions";
import { CashbackSummary } from "../accounts/types";
import { baseApi, transactionRipple } from "../api";
import { buildQueryParams } from "../utils";
import {
  transformTransactionPayload,
  transformTransactionsResponse,
} from "./selector";
import {
  CashflowSeriesPoint,
  GroupedTotal,
  TransactionDetails,
  TransactionsResult,
  TransactionsServerResponse,
  TransactionsTotals,
  TransactionWriteResult,
} from "./types";

interface UpdateTransactionArgs {
  transactionId: number;
  transaction: CreateTransactionsObject;
}

// RTK Query matches invalidations by exact `{ type, id }`, so the aggregate reads need their own explicit tag — a `LIST` invalidation would not refresh a bare `Transaction` provider.
const transactionsListTags = (result?: TransactionsResult) => [
  { type: "Transaction" as const, id: "LIST" },
  ...(result?.transactions.map((t) => ({
    type: "Transaction" as const,
    id: t.id,
  })) ?? []),
];

// Owned by `src/services/api/tags.ts` (loan payments and recurring transactions fire the same ripple); re-exported so existing import paths keep working.
export const invalidateTransaction = transactionRipple;

// `buildQueryParams` (not a raw `params` object) preserves the backend contract for repeated `transactionType` keys; the list opts out of the envelope unwrap (`unwrapResult: false`) so `transformResponse` can keep `pagination` alongside the rows.
export const transactionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCashbackSuggestion: builder.query<
      { cashbackEarned: number | null },
      {
        accountId: number;
        amount: number;
        transactionDate: string;
        excludeTransactionId?: number;
      }
    >({
      query: (params) => ({ url: "/transactions/cashback-suggestion", params }),
      keepUnusedDataFor: 0,
    }),
    getCashbackSummary: builder.query<CashbackSummary, number>({
      query: (accountId) => ({
        url: `/accounts/${accountId}/cashback-summary`,
      }),
      providesTags: (_result, _error, accountId) => [
        { type: "Cashback", id: accountId },
      ],
    }),
    getTransactions: builder.query<TransactionsResult, GetTransactionsProps>({
      query: (params) => {
        const base = `?sortBy=${encodeURIComponent(
          params.sortBy,
        )}&orderBy=${encodeURIComponent(
          params.orderBy,
        )}&page=${encodeURIComponent(params.page)}&limit=${encodeURIComponent(
          params.rowsPerPage,
        )}`;
        return {
          url: "/transactions" + buildQueryParams(base, params),
          method: "get",
          unwrapResult: false,
        };
      },
      transformResponse: (response: TransactionsServerResponse) =>
        transformTransactionsResponse(response),
      providesTags: (result) => transactionsListTags(result),
    }),

    getGroupedTransactions: builder.query<
      GroupedTotal[],
      GetGroupedTransactionsProps
    >({
      query: (params) => ({
        url: "/transactions/grouped" + buildQueryParams("", params),
        method: "get",
      }),
      providesTags: [{ type: "Transaction", id: "GROUPED" }],
    }),

    getTransactionsTotals: builder.query<
      TransactionsTotals,
      GetTransactionsTotalsProps
    >({
      query: (params) => ({
        url: "/transactions/totals" + buildQueryParams("", params),
        method: "get",
      }),
      providesTags: [{ type: "Transaction", id: "TOTALS" }],
    }),

    getCashflowSeries: builder.query<
      CashflowSeriesPoint[],
      GetCashflowSeriesProps
    >({
      query: (params) => ({
        url: "/transactions/series" + buildQueryParams("", params),
        method: "get",
      }),
      providesTags: [{ type: "Transaction", id: "SERIES" }],
    }),

    getTransaction: builder.query<TransactionDetails, number>({
      query: (transactionId) => ({ url: "/transactions/" + transactionId }),
      providesTags: (_result, _error, transactionId) => [
        { type: "Transaction", id: transactionId },
      ],
    }),

    // `unwrapResult: false`, as every holdings write does: the D8 cash warnings sit beside `result`, and the default unwrap would drop them silently.
    createTransaction: builder.mutation<
      TransactionWriteResult,
      CreateTransactionsObject
    >({
      query: (transaction) => ({
        url: "/transactions",
        method: "post",
        data: transformTransactionPayload(transaction),
        unwrapResult: false,
      }),
      invalidatesTags: () => invalidateTransaction(),
    }),

    updateTransaction: builder.mutation<unknown, UpdateTransactionArgs>({
      query: ({ transactionId, transaction }) => ({
        url: "/transactions/" + transactionId,
        method: "put",
        data: transformTransactionPayload(transaction),
      }),
      invalidatesTags: (_result, _error, { transactionId }) =>
        invalidateTransaction(transactionId),
    }),

    deleteTransaction: builder.mutation<unknown, number>({
      query: (transactionId) => ({
        url: "/transactions/" + transactionId,
        method: "delete",
      }),
      invalidatesTags: (_result, _error, transactionId) =>
        invalidateTransaction(transactionId),
    }),
  }),
});

export const {
  useLazyGetCashbackSuggestionQuery,
  useGetCashbackSummaryQuery,
  useGetTransactionsQuery,
  useLazyGetTransactionsQuery,
  useGetGroupedTransactionsQuery,
  useGetTransactionsTotalsQuery,
  useGetCashflowSeriesQuery,
  useGetTransactionQuery,
  useLazyGetTransactionQuery,
  useCreateTransactionMutation,
  useUpdateTransactionMutation,
  useDeleteTransactionMutation,
} = transactionsApi;
